import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ArrowLeft, Users, Image as ImageIcon, CalendarDays, Wallet, AlertOctagon,
         FileText as NotesIcon, Activity as ActivityIcon } from "lucide-react";

import { canEditCustomerNotes } from "../components/customers/customerUtils";
import { CustomerOverviewTab } from "../components/customers/CustomerOverviewTab";
import { CustomerBookingsTab } from "../components/customers/CustomerBookingsTab";
import { CustomerDepositsTab } from "../components/customers/CustomerDepositsTab";
import { CustomerIncidentsTab } from "../components/customers/CustomerIncidentsTab";
import { CustomerNotesTab } from "../components/customers/CustomerNotesTab";
import { CustomerActivityTab } from "../components/customers/CustomerActivityTab";

const TABS = [
  { id: "overview", label: "Overview", icon: ImageIcon },
  { id: "bookings", label: "Bookings", icon: CalendarDays },
  { id: "deposits", label: "Deposits", icon: Wallet },
  { id: "incidents", label: "Incidents", icon: AlertOctagon },
  { id: "notes", label: "Notes", icon: NotesIcon },
  { id: "activity", label: "Activity", icon: ActivityIcon },
];

export default function CustomerProfile() {
  const { email } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [tab, setTab] = useState("overview");

  const editAllowed = canEditCustomerNotes(user);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/customers/${encodeURIComponent(email)}`);
      setProfile(data);
    } catch (_) {
      setNotFound(true);
    }
  }, [email]);

  useEffect(() => { load(); }, [load]);

  if (notFound) {
    return (
      <div className="space-y-4" data-testid="customer-profile-not-found">
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-slate-500">No customer found with that email for this business.</div>
      </div>
    );
  }
  if (!profile) return <div className="text-slate-500" data-testid="customer-profile-loading">Loading…</div>;

  const rc = profile.returning_customer || {};

  return (
    <div className="space-y-6 max-w-5xl" data-testid="customer-profile-page">
      <button onClick={() => nav(-1)} className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900" data-testid="cp-back-btn">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold">{profile.customer?.name || email}</h1>
          <p className="text-sm text-slate-500 mt-0.5">{profile.customer?.email}</p>
        </div>
        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-medium" data-testid="cp-returning-badge">
          <Users className="w-3.5 h-3.5" />
          {rc.is_returning ? `Returning customer · ${rc.booking_count} previous bookings` : "First-time customer"}
        </span>
      </div>

      <div className="flex flex-wrap gap-1 p-1 rounded-xl bg-slate-100 w-fit" data-testid="cp-tabs">
        {TABS.map((tDef) => {
          const Icon = tDef.icon;
          const active = tab === tDef.id;
          return (
            <button
              key={tDef.id}
              onClick={() => setTab(tDef.id)}
              data-testid={`cp-tab-${tDef.id}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium inline-flex items-center gap-1.5 transition ${
                active ? "bg-white shadow-sm text-[#1B4332]" : "text-slate-600 hover:text-slate-900"
              }`}
            >
              <Icon className="w-3.5 h-3.5" /> {tDef.label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && <CustomerOverviewTab profile={profile} />}
      {tab === "bookings" && <CustomerBookingsTab bookings={profile.bookings || {}} />}
      {tab === "deposits" && <CustomerDepositsTab profile={profile} />}
      {tab === "incidents" && <CustomerIncidentsTab profile={profile} />}
      {tab === "notes" && <CustomerNotesTab email={email} editAllowed={editAllowed} />}
      {tab === "activity" && <CustomerActivityTab email={email} />}
    </div>
  );
}
